import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { Title } from "@angular/platform-browser";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { Color } from "@angular-material-components/color-picker";

import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash";

import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppMessage } from "src/app/models/common/appMessage";
import {
  createProductFamily,
  getProductFamiliesList,
  getProductFamily,
  resetProductFamily,
  updateProductFamily,
} from "src/app/store/actions/productFamilies/productFamilies.actions";
import {
  AppState,
  createdProductFamilyState,
  loadedFamilyIsLoadingState,
  loadedFamilyListState,
  loadedProductFamilyState,
  loginStateLocaleList,
  messagesStateGetMessage,
} from "src/app/store/reducers";

import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { FormUtils } from "src/app/shared/utils/formUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { ProductFamilyDTO } from "src/app/models/Products/productDTO";
import { CanComponentDeactivate } from "src/app/shared";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";
import { MatDialog } from "@angular/material/dialog";
import { UserLocaleDTO } from "src/app/models/UserDetails/userLocaleDTO";
import { buildDetailedDescription, populateDetailedDescription, setDetailedDescription, setFormAfterDialogClosed } from "src/app/shared/utils/localeDescriptionUtils";
import { DescriptionLocaleFormDialogComponent } from "src/app/shared/modules/dialogs/description-locale-form-dialog/description-locale-form-dialog.component";

export const FORM_PARAMS = {
  description: "description",
  detailedDescription: "detailedDescription",
  productParentFamilyCode: "productParentFamilyCode",
  productColor: "productColor",
  imageUrl: "imageUrl",
};
@Component({
  selector: "app-product-family-detail",
  templateUrl: "./product-family-detail.component.html",
  styleUrls: ["./product-family-detail.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductFamilyDetailComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  card: any;
  icon: any = "add";

  //Messages
  message$: Observable<AppMessage>;
  messageSubs: Subscription;

  //Family
  isLoading$: Observable<boolean>;
  loadedProductFamily$: Observable<ProductFamilyDTO>;
  productFamilyList$: Observable<ProductFamilyDTO[]>;
  productFamilyList: ProductFamilyDTO[];
  created$: Observable<boolean>;

  //Subscriptions
  loadedProductFamilySubs: Subscription;
  createdSub: Subscription;
  multiSubs: Subscription;

  //Locales
  localesList$: Observable<UserLocaleDTO[]>;
  localesListSub: Subscription;

  //Navigation
  currCode: string;

  currentProductFamily: ProductFamilyDTO;
  current: any;

  //Actions
  creation: boolean = false;
  duplication: boolean = false;

  //Family Objects
  loadedData: string;
  currentData: string;

  //Form
  familyDataForm: FormGroup;

  //Language
  langSubscription: Subscription;

  // loaded locale
  public locale: string = localStorage.getItem("locale");
  localeDesc: string;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private dialog: MatDialog
  ) {
    this.titleService.setTitle(this.translate.instant("ProductFamily.Details"));
    this.isLoading$ = this.store.pipe(select(loadedFamilyIsLoadingState));
    this.message$ = this.store.pipe(select(messagesStateGetMessage));
    this.loadedProductFamily$ = this.store.pipe(
      select(loadedProductFamilyState)
    );
    this.created$ = this.store.pipe(select(createdProductFamilyState));
    this.productFamilyList$ = this.store.pipe(select(loadedFamilyListState));
    this.localesList$ = this.store.pipe(select(loginStateLocaleList));

    //card config
    this.card = {
      avatarUrl: "",
      content: "",
      imageUrl: "",
      current: "",
      title: "ProductFamily.newFamily",
      subtitle: this.duplication
        ? "ProductFamily.duplication"
        : "ProductFamily.creation",
    };
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });
    
    //Initialize FormGroup
    this.createFamilyForm();
    this.loadFamilyInfo();
    this.loadLangChanges();
    this.resetCreation();

    this.loadLocales();
  }

  ngOnDestroy(): void {
    if (this.createdSub) {
      this.createdSub.unsubscribe();
    }
    if (this.loadedProductFamilySubs) {
      this.loadedProductFamilySubs.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  resetCreation() {
    this.createdSub = this.created$.pipe(skip(1)).subscribe((created) => {
      if (created) {
        this.creation = false;
        this.duplication = false;
      }
    });
  }
  
  loadLocales() {
    this.localesListSub = this.localesList$.subscribe((locales) => {
      if (locales) {
        this.localeDesc = locales.find(({ code }) => code === this.locale).description;
        buildDetailedDescription(
          locales, 
          this.fb,
          this.familyDataForm,
          FORM_PARAMS.detailedDescription,
        );
      }
    });
  }

  createFamilyForm() {
    this.familyDataForm = this.fb.group({
      [FORM_PARAMS.description]: [
        null,
        [Validators.required, FormUtils.noWhitespaceValidator, Validators.maxLength(100)],
      ],
      [FORM_PARAMS.detailedDescription]: this.fb.array([]),
      [FORM_PARAMS.productParentFamilyCode]: [null],
      [FORM_PARAMS.productColor]: [null],
      [FORM_PARAMS.imageUrl]: [null],
    });
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(
        this.translate.instant("ProductFamily.Details")
      );
    });
  }

  loadFamilyInfo() {
    this.multiSubs = zip(this.productFamilyList$.pipe(skip(1))).subscribe(
      ([productFamily]) => {
        if (
          productFamily &&
          productFamily?.length > 0 &&
          productFamily !== null &&
          productFamily !== undefined
        ) {
          this.productFamilyList = productFamily;

          this.productFamilyList = this.productFamilyList.map((family) => {
            return {
              code: family?.code,
              description: family?.detailedDescription[this.locale],
            };
          });
        }
        this.loadedProductFamilySubs = this.loadedProductFamily$.subscribe(
          (family) => {
            if (family) {
              this.currentProductFamily = cloneDeep(family);

              if (
                (!this.currCode ||
                  (this.currCode && this.currCode?.length === 0)) &&
                !this.duplication
              ) {
                this.currCode = this.currentProductFamily.code;
              }

              this.card = {
                avatarUrl: "",
                content: "",
                imageUrl: "",
                current: !this.duplication ? family?.code : "",
                title: !this.duplication
                  ? "[" +
                    family?.code +
                    "] - " +
                    (family?.code?.length > 0 ? family?.detailedDescription[this.locale] : "")
                  : "ProductFamily.newFamily",
                subtitle: !this.duplication
                  ? "ProductFamily.family"
                  : "ProductFamily.duplication",
              };

              this.familyDataForm.controls.description.setValue(
                family && !this.duplication
                  ? family?.detailedDescription[this.locale]
                  : ""
              );
              if (!this.duplication) {
                populateDetailedDescription(
                  this.familyDataForm.get("detailedDescription") as FormArray,
                  this.currentProductFamily.detailedDescription,
                );
              }

              let color = FormUtils.hexToRgb(family?.color);
              this.familyDataForm.controls.productColor.setValue(
                color && family?.color && !this.duplication
                  ? new Color(color.r, color.g, color.b)
                  : new Color(0, 0, 0)
              );
              this.familyDataForm.controls.imageUrl.setValue(
                family && family?.imageUrl && !this.duplication
                  ? family?.imageUrl
                  : ""
              );

              setTimeout(() => {
                if (!this.duplication) {
                  this.productFamilyList$.subscribe((family) => {
                    this.productFamilyList = family.filter(
                      (item) => item.code !== this.currCode
                    );
                  });
                }
                this.familyDataForm.patchValue(
                  {
                    productParentFamilyCode:
                      family && family?.parentProductFamilyCode
                        ? family?.parentProductFamilyCode
                        : "none",
                  },
                  { emitEvent: true, onlySelf: false }
                );
                this.loadedData = JSON.stringify(this.familyDataForm.value);
              });
            }
          }
        );
      }
    );
    this.store.dispatch(
      getProductFamiliesList({
        options: {
          paging: {
            skip: 0,
            top: 9999, // this.perPage
          },
          filteringArgs: undefined, // filteringExpr,
          sortingArgs: undefined, // sortingExpr
        },
      })
    );
  }

  initialiseState(code: string, duplicate?: boolean) {
    if (code) {
      this.loadFamily(code);
      if (duplicate) {
        this.currCode = "";
      } else {
        this.currCode = code;
      }
    } else {
      this.reset();
      this.currCode = "";
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "ProductFamily.newFamily",
        subtitle: this.duplication
          ? "ProductFamily.duplication"
          : "ProductFamily.creation",
      };
    }
  }

  loadFamily(code: string) {
    if (code && code?.length > 0) {
      this.store.dispatch(getProductFamily({ code: code }));
    }
  }

  getColorPickerBgColor(color: Color): string {
    if (color) {
      return getColorByBgColor(color.hex);
    }
  }

  openDescriptionLocaleFormDialog() {
    const detailedDescriptionBackup = [...this.familyDataForm.get("detailedDescription").value];
    const dialogData = {
      data: {
        parentForm: this.familyDataForm,
        formParam: FORM_PARAMS.detailedDescription,
      },
      width: '400px',
    };
    const dialogRef = this.dialog.open(DescriptionLocaleFormDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        setFormAfterDialogClosed(
          this.locale, 
          this.familyDataForm,
          FORM_PARAMS.detailedDescription,
          FORM_PARAMS.description
        );
      } else {
        this.familyDataForm.get("detailedDescription").setValue(detailedDescriptionBackup);
      }
    });
  }

  onSubmit() {
    this.currentData = JSON.stringify(this.familyDataForm.value);

    if (this.loadedData !== this.currentData) {
      if (this.familyDataForm.valid) {
        if (
          this.familyDataForm.controls.productParentFamilyCode.value === "none"
        ) {
          this.familyDataForm.controls.productParentFamilyCode.setValue(null);
        }

        let productFamily: any = {
          description: this.familyDataForm.controls.description.value ?? null,
          detailedDescription: setDetailedDescription(
            this.familyDataForm, 
            FORM_PARAMS.detailedDescription, 
            FORM_PARAMS.description, 
            this.locale
          ),
          color: this.familyDataForm.controls.productColor.value
            ? this.familyDataForm.controls.productColor.value?.hex
            : "",
          imageUrl: this.familyDataForm.controls.imageUrl.value
            ? this.familyDataForm.controls.imageUrl.value
            : "",
          parentProductFamilyCode: this.familyDataForm.controls
            .productParentFamilyCode.value
            ? this.familyDataForm.controls.productParentFamilyCode.value
            : null,
        };
        if (this.currCode.length === 0) {
          this.store.dispatch(createProductFamily({ productFamily }));
        } else {
          productFamily = { ...productFamily, code: this.currCode };
          this.store.dispatch(updateProductFamily({ productFamily }));
        }
        this.familyDataForm.markAsUntouched();
        this.familyDataForm.markAsPristine();
      } else {
        this.familyDataForm.markAllAsTouched();
        this.snackbarService.show({
          message: this.translate.instant("ProductFamily.invalid"),
          type: SnackbarType.ERROR,
        });
        return;
      }
    } else {
      this.snackbarService.show({
        message: this.translate.instant("ProductFamily.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }
  }

  reset() {
    this.store.dispatch(resetProductFamily());
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  refresh() {
    if (isAuthenticated(this.router.url) && !this.familyDataForm.pristine) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }
  
  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["families/family", this.currCode]);
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currCode);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["families/family", {}]);
  }

  new() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["/families/family"]);
    this.creation = true;
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.familyDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
}
